import {
  activateOnboardingRuntimeStarter,
  getTokenStatus,
  updateDefaultRuntime,
} from "@/api/companion";
import type { AvailableRuntime } from "@/stores/companion-store";
import { useCompanionStore } from "@/stores/companion-store";
import { useAgentStore } from "@/stores/agent-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { prewarmConnectedOnboardingRuntime } from "./onboarding-session-prewarm";
import {
  FirstPromptPreparationError,
  prepareFirstRuntimePrompt,
} from "./prepare-first-prompt";
import { captureDesktopChatAuthScopeGuard } from "../desktop-chat-auth-scope";
import { runtimeText } from "./runtime-setup-copy";
import {
  captureOnboardingProgressGuard,
  useOnboardingStore,
} from "@/stores/onboarding-store";
import {
  getOnboardingItem,
  removeOnboardingItem,
  setOnboardingBool,
  setOnboardingItem,
} from "@/lib/onboarding-local-state";

const PENDING_MODEL_KEY = "denker.onboarding.pending_opencode_model";
const PENDING_RUNTIME_KEY = "denker.onboarding.pending_runtime";

function captureSetupGuard(): () => void {
  const currentAuth = captureDesktopChatAuthScopeGuard();
  const currentProgress = captureOnboardingProgressGuard();
  const step = useOnboardingStore.getState().step;
  return () => {
    const state = useOnboardingStore.getState();
    if (
      !currentAuth() ||
      !currentProgress() ||
      state.step !== step ||
      state.restarting ||
      state.pendingCompletion
    )
      throw new FirstPromptPreparationError(runtimeText("setupChanged"));
  };
}

export function isRuntimeType(
  value: string | null,
): value is AvailableRuntime["type"] {
  return (
    value === "claude_cli" || value === "codex_cli" || value === "opencode_cli"
  );
}

// Single source of truth for "this is a runtime the user installed
// themselves" — a Denker-bundled/managed runtime (e.g. the starter opencode)
// never counts. Consumed by the picker UI, the apply path, runtime-health
// evaluation, and update/detection polling so they cannot disagree about
// which runtimes are personal vs. bundled.
export function isPersonalRuntime(runtime: AvailableRuntime): boolean {
  return !runtime.managed && runtime.source !== "denker_starter";
}

// Single source of truth for the picker's visible-choice filter — consumed by
// both the picker UI (RuntimeSetupSurface) and the apply path so they cannot
// disagree about which runtimes are committable (e.g. managed bundled
// opencode is hidden in the UI and never committed by apply).
export function visibleRuntimeChoices(
  runtimes: readonly AvailableRuntime[],
): AvailableRuntime[] {
  return runtimes.filter(isPersonalRuntime);
}

export function personalRuntime(
  runtimes: readonly AvailableRuntime[],
  runtimeType: AvailableRuntime["type"],
): AvailableRuntime | undefined {
  return runtimes.find(
    (runtime) => runtime.type === runtimeType && isPersonalRuntime(runtime),
  );
}

export function managedStarterRuntime(
  runtimes: readonly AvailableRuntime[],
): AvailableRuntime | null {
  return (
    runtimes.find(
      (runtime) =>
        runtime.type === "opencode_cli" &&
        (runtime.managed || runtime.source === "denker_starter"),
    ) ?? null
  );
}

export function readPendingOnboardingRuntime():
  | AvailableRuntime["type"]
  | null {
  const value = getOnboardingItem(PENDING_RUNTIME_KEY);
  return isRuntimeType(value) ? value : null;
}

export function setPendingOnboardingRuntime(
  runtime: AvailableRuntime["type"],
  model?: string,
): void {
  setOnboardingItem(PENDING_RUNTIME_KEY, runtime);
  if (runtime === "opencode_cli" && model)
    setOnboardingItem(PENDING_MODEL_KEY, model);
  else removeOnboardingItem(PENDING_MODEL_KEY);
}

export function clearPendingOnboardingRuntime(): void {
  removeOnboardingItem(PENDING_RUNTIME_KEY);
  removeOnboardingItem(PENDING_MODEL_KEY);
}

export async function applyPendingOnboardingRuntime({
  verifyServer = false,
  prepareFirstPrompt = false,
}: {
  verifyServer?: boolean;
  prepareFirstPrompt?: boolean;
} = {}): Promise<void> {
  const assertCurrent = captureSetupGuard();
  assertCurrent();
  const store = useCompanionStore.getState();
  const choices = visibleRuntimeChoices(store.availableRuntimes);
  const isVisible = (value: string | null) =>
    !!value && choices.some((r) => r.type === value && r.logged_in === true);
  // Prefer the user's explicit selection when it is still a visible choice;
  // otherwise fall back to the runtime the picker displayed as pre-selected
  // (the Rust-detected first-installed CLI) so a user who hits Continue
  // without clicking — or whose prior pick is no longer available — still
  // gets a working default.
  const pending = readPendingOnboardingRuntime();
  const fallback = isRuntimeType(store.defaultRuntime)
    ? store.defaultRuntime
    : null;
  const runtime = isVisible(pending)
    ? pending
    : isVisible(fallback)
      ? fallback
      : null;
  if (!runtime) {
    if (pending) removeOnboardingItem(PENDING_RUNTIME_KEY);
    throw new Error("Choose a signed-in runtime before continuing.");
  }
  if (verifyServer) {
    store.sendRuntimeRefresh?.(false);
    let detected = false;
    for (let attempt = 0; attempt < 10; attempt += 1) {
      const info = await getTokenStatus();
      assertCurrent();
      detected = visibleRuntimeChoices(info.available_runtimes ?? []).some(
        (entry) => entry.type === runtime && entry.logged_in === true,
      );
      if (detected) break;
      if (attempt < 9) await new Promise((resolve) => setTimeout(resolve, 300));
    }
    if (!detected) {
      throw new Error(
        "Your connection is still syncing. Choose your provider again to retry.",
      );
    }
  }
  const model =
    runtime === "opencode_cli" ? getOnboardingItem(PENDING_MODEL_KEY) : null;
  assertCurrent();
  const selected = verifyServer
    ? await updateDefaultRuntime(runtime, model ?? undefined, {
        onboardingRecovery: true,
      })
    : model
      ? await updateDefaultRuntime(runtime, model)
      : await updateDefaultRuntime(runtime);
  assertCurrent();
  if (selected !== runtime) {
    throw new Error("The selected runtime was not activated.");
  }
  store.setAvailableRuntimes(store.availableRuntimes, selected);
  await useAgentStore.getState().fetchAllTemplates();
  assertCurrent();
  if (prepareFirstPrompt) {
    // Restart preserves old chats; prepare and submit the lesson to a fresh one.
    useInputBarTarget.getState().requestNewConversation();
    await prepareFirstRuntimePrompt(runtime);
    assertCurrent();
  } else prewarmConnectedOnboardingRuntime();
  removeOnboardingItem(PENDING_RUNTIME_KEY);
  removeOnboardingItem(PENDING_MODEL_KEY);
}

export async function activateManagedStarterForOnboarding(): Promise<void> {
  const assertCurrent = captureSetupGuard();
  assertCurrent();
  const selected = await activateOnboardingRuntimeStarter();
  assertCurrent();
  if (selected !== "opencode_cli") {
    throw new Error("The bundled onboarding runtime was not activated.");
  }
  const info = await getTokenStatus();
  assertCurrent();
  const runtimes = info.available_runtimes ?? [];
  if (!managedStarterRuntime(runtimes))
    throw new Error(
      "The included AI is still connecting. Try again or click Next.",
    );
  const store = useCompanionStore.getState();
  store.setAvailableRuntimes(runtimes, selected);
  if (info.available_models) store.setAvailableModels(info.available_models);
  await useAgentStore.getState().fetchAllTemplates();
  assertCurrent();
  useInputBarTarget.getState().requestNewConversation();
  await prepareFirstRuntimePrompt("opencode_cli", "denker_starter");
  assertCurrent();
  removeOnboardingItem(PENDING_RUNTIME_KEY);
  removeOnboardingItem(PENDING_MODEL_KEY);
}

export function markPostDemoRuntimeHandled(): void {
  setOnboardingBool("denker.onboarding.trial-banner-dismissed", true);
}
